import {
  CircularProgress,
  InputLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import { FieldTemplate, LayoutItem } from 'api/models';
import SelectableTableFieldRow from './SelectableTableFieldRow';
import { useTranslation } from 'react-i18next';
import useRequest from 'hooks/useRequest';
import { FieldAttributesOptions } from 'api/models/FieldTemplate';
import { GeneralIds, ModelTypes } from 'types/types';
import { useParams } from 'react-router-dom';
import { Box } from '@mui/system';
import { query } from 'operations';
import useEvaluateConditions from 'hooks/useEvaluateConditions';

interface SelectableTableProps {
  fields: readonly FieldTemplate[];
  onDelete?: (index: number) => void;
  onEdit?: (field: FieldTemplate) => void;
  onChange?: (event: any) => void;
  formik?: any;
  children?: LayoutItem[];
  path?: string;
  disabled?: boolean;
  isReadOnly?: boolean;
  item?: LayoutItem;
  externalFieldValues: {
    [key: string]: string | number | boolean | string[] | null;
  };
}
export default function SelectableTableField({
  fields,
  onEdit,
  onDelete,
  formik,
  children,
  path,
  disabled,
  isReadOnly,
  item,
  externalFieldValues,
}: SelectableTableProps) {
  const isVisible = useEvaluateConditions({
    values: { ...externalFieldValues, ...formik?.values },
    conditionGroup: item?.layoutOptions?.isVisible,
  });

  const { t } = useTranslation();
  const { projectId } = useParams<GeneralIds>();

  const field = fields[0];
  const optionsSourceFieldTemplateId =
    field?.fieldAttributes?.optionsSourceFieldTemplateId;

  const { data: referedOptions, loading } = useRequest<
    FieldAttributesOptions[]
  >(
    async () => {
      if (!optionsSourceFieldTemplateId) return field.fieldAttributes?.options;
      const { data: fields } = await query<FieldTemplate[]>({
        type: ModelTypes.fieldTemplate,
        ids: {
          projectId,
          id: optionsSourceFieldTemplateId,
        },
      });

      const optionField = fields[0];
      return optionField?.fieldAttributes.options;
    },
    {
      dependencies: [
        optionsSourceFieldTemplateId,
        field.fieldAttributes?.options,
      ],
    },
  );

  const options =
    (field?.fieldAttributes?.options?.length ?? 0) > 0
      ? field?.fieldAttributes?.options
      : referedOptions ?? [];

  const fieldIdMap = new Map(fields.map(field => [field.id, field]));
  const hasErrors = !!(
    formik?.errors &&
    Object.keys(formik.errors).some(errorId => fieldIdMap.has(errorId))
  );

  if (item && !isVisible) {
    return <></>;
  }

  return (
    <>
      <TableContainer
        sx={{
          border: theme => `1px solid ${theme.palette.divider}`,
          borderRadius: 1.5,
          mb: 1,
        }}
      >
        <Table>
          <TableHead>
            <TableRow>
              <TableCell></TableCell>
              {!loading &&
                options?.map(option => (
                  <TableCell
                    align="center"
                    key={`selectable-table-field-cell-${option.label}-${option.value}`}
                  >
                    {option.label}
                  </TableCell>
                ))}
              {onEdit && <TableCell align="right"></TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {fields.map((field, rowIndex) => {
              const isError = Boolean(formik?.errors[field.id]);
              return (
                <SelectableTableFieldRow
                  key={`row-selectable-table-field-row-${field.id}`}
                  rowIndex={rowIndex}
                  field={field}
                  isError={isError}
                  onEdit={onEdit}
                  onDelete={onDelete}
                  formik={formik}
                  isLastRow={rowIndex === fields.length - 1}
                  disabled={disabled}
                  isReadOnly={isReadOnly}
                  externalFieldValues={externalFieldValues}
                />
              );
            })}
          </TableBody>
        </Table>

        <Box
          sx={{
            position: 'absolute',
            width: '100%',
            height: '100%',
            top: 0,
            left: 0,
            backdropFilter: 'blur(2px)',
            cursor: 'not-allowed',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            transition: 'all 0.3s ease',
            opacity: loading ? 1 : 0,
            zIndex: loading ? 100 : -1,
          }}
        >
          <CircularProgress />
        </Box>
      </TableContainer>
      {hasErrors && (
        <InputLabel error>{t('general.errors.required')}</InputLabel>
      )}
    </>
  );
}
